import { useMemo } from 'react';

import { type ConversationAction } from '@atlassian/rovo-conversation-actions';

import {
	isNudgeByAction,
	type NudgeEntry,
} from '@confluence/page-rovo-config-common/entry-points/nudgeTypes.ts';
import {
	type AgentMentionContext,
	useAgentMentionNudgeAction,
} from '@confluence/page-rovo-config-common/entry-points/useAgentMentionNudgeAction.ts';
import { useViewPageRovoActions } from '@confluence/page-rovo-config-common/entry-points/useViewPageRovoActions.ts';

const AUDIO_BRIEFING_MIN_READ_TIME_MINUTES = 5;

/**
 * Returns nudge actions for the edit page: the agent mention nudge plus live-doc-only nudges.
 */
export function useEditPageNudgeConfig(
	enableLiveDocNudges: boolean,
	eligibleForChangeboarding: boolean,
	isFirstVisit: boolean,
	isCurrentUserContributor: boolean,
	isEligibilityLoading: boolean,
	readTimeMinutes: number | null,
	/**
	 * controls nudge *presence* (truthy = mention exists). Presence also requires
	 * `resolvedAgentMentionId` below, so the nudge never renders (and can't be clicked) before
	 * the Agent Studio UUID has resolved — clicking before resolution would fire `chat-new`
	 * without a routable agentId.
	 */
	agentMentionId?: string | null,
	/** control nudge *presence* (see above) and *payload* (forwarded to useAgentMentionNudgeAction). */
	resolvedAgentMentionId?: string | null,
	/** control nudge *payload* (forwarded to useAgentMentionNudgeAction). */
	agentMentionContext?: AgentMentionContext | null,
	/** control nudge fallback prompt (forwarded to useAgentMentionNudgeAction). */
	agentMentionPrompt?: string | null,
	/** control nudge *payload* (forwarded to useAgentMentionNudgeAction). */
	agentMentionParentNodeType?: string | null,
	/** controls the nudge action id so each inserted mention is treated as a new nudge. */
	agentMentionInsertionCount?: number,
	/** control nudge title (forwarded to useAgentMentionNudgeAction). */
	agentMentionName?: string | null,
): ConversationAction[] {
	const viewPageRovoActions = useViewPageRovoActions();
	const agentMentionNudgeAction = useAgentMentionNudgeAction(
		resolvedAgentMentionId,
		agentMentionContext,
		agentMentionPrompt,
		agentMentionParentNodeType,
		agentMentionInsertionCount,
		agentMentionName,
		agentMentionId,
	);

	const actionsById = useMemo(
		() =>
			new Map<string, ConversationAction>(
				viewPageRovoActions.map(
					(registeredAction: { actionId: string; action: ConversationAction }) => [
						registeredAction.actionId,
						registeredAction.action,
					],
				),
			),
		[viewPageRovoActions],
	);

	return useMemo(() => {
		// Require resolvedAgentMentionId too: without it, a click fired before the AAID→UUID
		// resolution completes would publish `chat-new` with no agentId, silently routing to
		// the default/current conversation agent instead of the mentioned one.
		const alwaysOnNudges: ConversationAction[] =
			agentMentionId && resolvedAgentMentionId ? [agentMentionNudgeAction] : [];

		if (!enableLiveDocNudges) return alwaysOnNudges;

		const isAudioBriefingEligible =
			!isEligibilityLoading &&
			!eligibleForChangeboarding &&
			isFirstVisit &&
			!isCurrentUserContributor &&
			readTimeMinutes !== null &&
			readTimeMinutes >= AUDIO_BRIEFING_MIN_READ_TIME_MINUTES;

		const nudgeEntries: NudgeEntry[] = [
			{ actionId: 'audio-briefing', enabled: isAudioBriefingEligible },
		];

		const liveDocNudges = nudgeEntries.reduce<ConversationAction[]>((acc, nudgeEntry) => {
			if (!nudgeEntry.enabled) return acc;
			const action = isNudgeByAction(nudgeEntry)
				? nudgeEntry.action
				: actionsById.get(nudgeEntry.actionId);
			if (action) acc.push(action);
			return acc;
		}, []);

		return [...alwaysOnNudges, ...liveDocNudges];
	}, [
		agentMentionId,
		// resolvedAgentMentionId now also gates presence (see above), so it must be a dep.
		// agentMentionContext/agentMentionParentNodeType/agentMentionName remain payload-only:
		// agentMentionNudgeAction already invalidates on those values.
		resolvedAgentMentionId,
		agentMentionNudgeAction,
		enableLiveDocNudges,
		isEligibilityLoading,
		eligibleForChangeboarding,
		isFirstVisit,
		isCurrentUserContributor,
		readTimeMinutes,
		actionsById,
	]);
}
